import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Download, Upload } from "lucide-react";
import { useLocation } from "wouter";
import { format } from "date-fns";

export default function ImportHistory() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  
  const historyQuery = trpc.import.history.useQuery();

  const downloadErrorReport = async (id: number, fileName: string) => {
    const utils = trpc.useUtils();
    const errors = await utils.client.import.getErrorReport.query({ id }) as any[];
    
    const csv = "Row,Field,Error\n" + errors.map((e: any) => 
      `${e.row},"${e.field}","${e.message}"`
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `errors-${fileName}-${id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Only administrators can view import history</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-xl font-bold">Import History</h1>
            </div>
            <Button onClick={() => navigate("/csv-import")}>
              <Upload className="w-4 h-4 mr-2" />
              New Import
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-6xl">
        {historyQuery.isLoading && (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        )}

        {historyQuery.data && historyQuery.data.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No import history yet. Start by importing your first CSV file.
            </CardContent>
          </Card>
        )}

        {historyQuery.data && historyQuery.data.length > 0 && (
          <div className="space-y-4">
            {historyQuery.data.map((record: any) => (
              <Card key={record.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{record.fileName}</CardTitle>
                      <CardDescription>
                        Imported {format(new Date(record.importedAt), "PPp")}
                      </CardDescription>
                    </div>
                    <Badge variant={
                      record.status === 'completed' ? 'default' :
                      record.status === 'partial' ? 'secondary' :
                      'destructive'
                    }>
                      {record.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Total Rows</div>
                      <div className="text-2xl font-bold">{record.totalRows}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Successful</div>
                      <div className="text-2xl font-bold text-green-600">{record.successCount}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Failed</div>
                      <div className="text-2xl font-bold text-red-600">{record.failedCount}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Success Rate</div>
                      <div className="text-2xl font-bold">
                        {Math.round((record.successCount / record.totalRows) * 100)}%
                      </div>
                    </div>
                  </div>

                  {record.errorReport && record.errorReport.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadErrorReport(record.id, record.fileName)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Error Report ({record.errorReport.length} errors)
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
